<script setup lang="ts">
import { isInstagramBrowserAgent } from '@/utils';
import { useWebsiteJsonLd } from '~/composables/jsonLd/useWebsiteJsonLd';
import { useAuthCrossTabSync } from '~/composables/useAuthCrossTabSync';
import { SHORTS_FIRST_FRAME_EVENT } from '~/utils/shortsEvents';
import { nuxtRegionService } from '~~/adapters/services/nuxt/nuxtRegionService';
import { appName, siteName } from '~~/app/constants';
import { REGION_SERVICE } from '~~/utils/symbols';
import { ConfigProvider } from 'reka-ui';

import Toaster from './components/ui/toast/Toaster.vue';
import { useEvents } from './composables/events/useEvents';
import { useCanonical } from './composables/useCanonical';

provide(REGION_SERVICE, nuxtRegionService);

// Initialize cross-tab auth sync globally so all tabs listen for auth changes
useAuthCrossTabSync();

// LoginModal é montado só quando aberto (defer). Tira o modal de login +
// useSocialLogin + deps de form/dialog do bundle de entrada (entry.js), carregando
// o chunk sob demanda no 1º "Entrar". O listener cross-tab fica acima (app.vue), e o
// modal só dispara notifyLogin() ao logar — então adiar o mount não perde efeito.
const LoginModal = defineAsyncComponent(() =>
	import('./components/LoginModal').then(m => m.LoginModal)
);

// Mesmo racional do LoginModal: o banner so aparece segundos depois do load.
const CookiesWarning = defineAsyncComponent(() =>
	import('./components/CookiesBanner').then(m => m.CookiesWarning)
);
const { modalState: loginModalState } = useLoginModal();

const { loggedIn: isUserLoggedIn } = useUserSession();
const router = useRouter();

watchEffect(async () => {
	const isProtectedRoute = router.currentRoute.value.meta.middleware === 'auth';

	if (isProtectedRoute && !isUserLoggedIn.value) await router.push('/');
});

useWebsiteJsonLd();
const { canonicalUrl } = useCanonical();

useHead({
	title: appName,
	htmlAttrs: { lang: 'pt-BR' },
	link: () => [
		// Only add canonical link if it's different from current URL
		...(canonicalUrl.value
			? [
					{
						rel: 'canonical',
						href: canonicalUrl.value
					}
				]
			: [])
		// NOTE: preconnect para maps.googleapis.com removido daqui. Era global,
		// mas o Maps só é usado em telas específicas (a home não pede maps), então
		// o PageSpeed marcava como "preconnect não usado". Telas que usam Google
		// Maps devem declarar o preconnect na própria página.
	]
});
useSeoMeta({ ogSiteName: siteName });

const { cookiesAccepted } = useAllowCookies();

const { trackCookiesAccepted } = useEvents();
const cookiesAcceptedCleanup = onClientEventBus(
	useEventBus('cookies-accepted'),
	trackCookiesAccepted
);

const isNotTermsPage = computed(
	() => !router.currentRoute.value.path.startsWith('/termos')
);

const shouldShowBanner = ref(false);
const isInstagramBrowser = ref(false);

const hotjarConfig = useRuntimeConfig().public.hotjar as {
	hotjarId: number;
	scriptVersion: number;
	debug: boolean;
};

let hotjarInitialized = false;

function initHotjarWhenIdle() {
	if (!import.meta.client || typeof window === 'undefined') return;
	if (hotjarInitialized || !hotjarConfig.hotjarId) return;
	hotjarInitialized = true;

	const doInit = async () => {
		const { default: Hotjar } = await import('@hotjar/browser');
		Hotjar.init(hotjarConfig.hotjarId, hotjarConfig.scriptVersion, {
			debug: hotjarConfig.debug
		});
	};

	const interactionEvents = [
		'pointerdown',
		'keydown',
		'scroll',
		'touchstart'
	] as const;
	let fallbackTimer: ReturnType<typeof setTimeout> | undefined;

	const trigger = () => {
		for (const event of interactionEvents) {
			window.removeEventListener(event, trigger);
		}
		if (fallbackTimer !== undefined) clearTimeout(fallbackTimer);

		if ('requestIdleCallback' in window) {
			requestIdleCallback(() => doInit(), { timeout: 2000 });
		} else {
			doInit();
		}
	};

	for (const event of interactionEvents) {
		window.addEventListener(event, trigger, { passive: true, once: true });
	}
	fallbackTimer = setTimeout(trigger, 5000);
}

const isCookiesNotAccepted = computed(
	() => !cookiesAccepted.value || cookiesAccepted.value === 'not accepted'
);

onMounted(() => {
	if (cookiesAccepted.value === 'accepted') {
		initHotjarWhenIdle();
	}

	isInstagramBrowser.value = isInstagramBrowserAgent();

	// Nos shorts o banner espera o video comecar (ou 10s): o texto dele baixa a
	// fonte (65KB) e rouba banda do primeiro chunk.
	const showBannerWhenAllowed = () =>
		(shouldShowBanner.value =
			isNotTermsPage.value && isCookiesNotAccepted.value);
	if (router.currentRoute.value.path.startsWith('/shorts/')) {
		const off = useEventListener(
			window,
			SHORTS_FIRST_FRAME_EVENT,
			() => {
				off();
				showBannerWhenAllowed();
			},
			{ once: true }
		);
		setTimeout(showBannerWhenAllowed, 10000);
	} else {
		nextTick(showBannerWhenAllowed);
	}
});

watch(cookiesAccepted, () => {
	shouldShowBanner.value = isNotTermsPage.value && isCookiesNotAccepted.value;

	if (cookiesAccepted.value === 'accepted') {
		initHotjarWhenIdle();
	}

	if (isInstagramBrowser.value && cookiesAccepted.value === 'accepted') {
		shouldShowBanner.value = false;
	}
});

onUnmounted(() => {
	cookiesAcceptedCleanup();
});

const useIdFunction = () => useId();
</script>

<template>
	<ConfigProvider :use-id="useIdFunction">
		<NuxtLayout>
			<Toaster />
			<LoginModal v-if="loginModalState.isOpen" />
			<NuxtPage />
			<Transition
				enter-active-class="transition-[transform,opacity] duration-300 ease-out"
				enter-from-class="translate-y-full opacity-0"
				enter-to-class="translate-y-0 opacity-100"
				leave-active-class="transition-[transform,opacity] duration-300 ease-in"
				leave-from-class="translate-y-0 opacity-100"
				leave-to-class="translate-y-full opacity-0"
			>
				<div
					v-if="shouldShowBanner"
					class="pointer-events-auto fixed inset-x-0 bottom-0 z-[101] w-full"
				>
					<CookiesWarning />
				</div>
			</Transition>
		</NuxtLayout>
	</ConfigProvider>
</template>
